// Zoom / pan / fullscreen controller for a page image inside a scrolling
// viewport. Ported from toseftasite, which had the more careful of the two
// implementations; taanticMidrashimDig's fixed zoom-step version is replaced
// by this.
//
// Zoom is applied as a real layout width (a percentage, so it's relative to
// the container and needs no manual pixel math), NOT a CSS transform.
// transform:scale() only visually enlarges the element -- it doesn't change
// its layout box, and browsers largely don't grow a scrolling ancestor's
// horizontal scrollable-overflow region for that (confirmed: scrollWidth
// stayed pinned to clientWidth at any zoom level under transform, while
// scrollHeight DID grow -- an inconsistent, unreliable asymmetry). An actual
// width resize makes native scrollbars / trackpad / drag-panning work
// correctly in both directions, with no special-casing.

export const ZOOM_DEFAULTS = {
    min: 0.4,
    max: 6,
    focus: 3,      // zoom applied when a word is focused
    step: 1.25,    // per button press
    wheelStep: 1.08,
};

export function createZoomController(viewport, opts = {}) {
    const cfg = { ...ZOOM_DEFAULTS, ...opts };
    let zoom = 1;
    let wrapEl = null, boxEl = null;
    // Width of the wrap, as a percentage of the viewport, at zoom 1. Normally
    // 100 (the page image fills the viewport), but a host whose "fitted" view
    // is not the whole image sets its own: br-viewer fits ONE TEXT COLUMN of a
    // manuscript scan to the pane, so at zoom 1 a page whose column is a third
    // of the scan needs a wrap 300% wide. Per-attach, since it varies by page.
    let baseWidthPct = 100;

    function clamp(z) { return Math.min(cfg.max, Math.max(cfg.min, z)); }

    function applyZoomWidth() {
        if (!wrapEl) return;
        wrapEl.style.width = (zoom * baseWidthPct) + '%';
    }

    // A Hebrew page computes direction:rtl, so the viewport's scrollLeft
    // follows the "negative" RTL scrolling model every modern browser now
    // implements: 0 = home = viewport's LEFT edge sits at physical content-X
    // (scrollWidth - clientWidth); more negative reveals content further
    // toward physical-X 0. offsetLeft/offsetTop are always physical
    // (direction-independent), so converting a desired physical
    // viewport-left-edge X into scrollLeft needs this offset subtracted --
    // using the *positive* LTR-style formula directly (as a first version of
    // this code did) computes wildly out-of-range targets that silently clamp
    // to 0, which looked like "horizontal scroll/centering does nothing".
    function physicalLeftToScrollLeft(physicalX) {
        if (!isRtl()) return physicalX;
        return physicalX - (viewport.scrollWidth - viewport.clientWidth);
    }

    function isRtl() {
        return getComputedStyle(viewport).direction === 'rtl';
    }

    // Re-center the scroll position on the focused word's highlight box at the
    // current zoom. Since the box is positioned with PERCENTAGES against the
    // wrap, offsetLeft/Top/Width/Height already reflect its real, current
    // on-screen pixel position at whatever width the wrap now has -- no
    // separate *zoom factor needed (unlike the old transform approach).
    // Box position within the wrap. offsetLeft/Top are relative to the box's
    // offsetParent, which for a stacked canvas (bifolio pages: one
    // position:relative div per folio) is that canvas, not the wrap -- so
    // walk up to the wrap, or boxes on the 2nd+ canvas scroll to the wrong place.
    function boxOffsetInWrap() {
        let x = 0, y = 0, el = boxEl;
        while (el && el !== wrapEl) { x += el.offsetLeft; y += el.offsetTop; el = el.offsetParent; }
        return el === wrapEl ? { x, y } : { x: boxEl.offsetLeft, y: boxEl.offsetTop };
    }
    function centerOnBox(behavior = 'smooth') {
        if (!wrapEl || !boxEl) return;
        applyZoomWidth();
        const off = boxOffsetInWrap();
        const boxCenterX = off.x + boxEl.offsetWidth / 2;
        const targetLeft = physicalLeftToScrollLeft(boxCenterX - viewport.clientWidth / 2);
        const targetTop = off.y + boxEl.offsetHeight / 2 - viewport.clientHeight / 2;
        viewport.scrollTo({ left: targetLeft, top: targetTop, behavior });
    }

    // Like centerOnBox, but the box's top edge goes to the top of the
    // viewport: for framing a whole column, where the reader starts at its top.
    function topOnBox(behavior = 'smooth') {
        if (!wrapEl || !boxEl) return;
        applyZoomWidth();
        const off = boxOffsetInWrap();
        const boxCenterX = off.x + boxEl.offsetWidth / 2;
        viewport.scrollTo({
            left: physicalLeftToScrollLeft(boxCenterX - viewport.clientWidth / 2),
            top: Math.max(0, off.y - 12), behavior,
        });
    }

    function setZoomAtPoint(newZoom, clientX, clientY) {
        if (!wrapEl) return;
        newZoom = clamp(newZoom);
        const rect = viewport.getBoundingClientRect();
        const cx = clientX - rect.left, cy = clientY - rect.top;
        // Work in fractions of content width/height so the RTL offset only
        // has to be applied once, at the very end, when writing scrollLeft.
        const oldScrollWidth = viewport.scrollWidth;
        const physX = isRtl()
            ? (oldScrollWidth - viewport.clientWidth + viewport.scrollLeft) + cx
            : viewport.scrollLeft + cx;
        const fracX = physX / oldScrollWidth;
        const fracY = (viewport.scrollTop + cy) / viewport.scrollHeight;
        zoom = newZoom;
        applyZoomWidth();
        viewport.scrollLeft = physicalLeftToScrollLeft(fracX * viewport.scrollWidth - cx);
        viewport.scrollTop = fracY * viewport.scrollHeight - cy;
    }

    function stepZoom(factor) {
        if (!wrapEl) return;
        zoom = clamp(zoom * factor);
        if (boxEl) centerOnBox();
        else applyZoomWidth();
    }

    function reset() {
        zoom = 1;
        if (boxEl) centerOnBox();
        else if (wrapEl) {
            applyZoomWidth();
            viewport.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        }
    }

    // Ctrl/pinch + wheel zooms at the pointer (trackpad pinch is reported as a
    // wheel event with ctrlKey by every major browser); plain wheel keeps its
    // native scroll/pan behavior untouched.
    function onWheel(e) {
        if (!e.ctrlKey || !wrapEl) return;
        e.preventDefault();
        setZoomAtPoint(zoom * (e.deltaY < 0 ? cfg.wheelStep : 1 / cfg.wheelStep), e.clientX, e.clientY);
    }

    let dragging = false, lastX = 0, lastY = 0;
    function onMouseDown(e) {
        if (!wrapEl || e.button !== 0) return;
        dragging = true;
        lastX = e.clientX; lastY = e.clientY;
        viewport.classList.add('mvc-dragging');
        e.preventDefault();
    }
    function onMouseMove(e) {
        if (!dragging) return;
        viewport.scrollLeft -= (e.clientX - lastX);
        viewport.scrollTop -= (e.clientY - lastY);
        lastX = e.clientX; lastY = e.clientY;
    }
    function onMouseUp() {
        dragging = false;
        viewport.classList.remove('mvc-dragging');
    }

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return {
        // Called by the panel after each re-render, with the new elements
        // (box may be null when the word has no recognised box).
        // `base` (optional) is the zoom-1 wrap width as a percentage of the
        // viewport; omitted, the previous value is kept (100 by default).
        attach(wrap, box, base) {
            wrapEl = wrap; boxEl = box;
            if (base != null && isFinite(base) && base > 0) baseWidthPct = base;
        },
        get baseWidth() { return baseWidthPct; },
        detach() { wrapEl = null; boxEl = null; },
        // Frame the attached box. `fit` (used for a wide target like a text
        // line) chooses the zoom that makes the box span the viewport instead
        // of the fixed cfg.focus factor, which is tuned for a single word and
        // would leave a full line either overflowing or lost in the page.
        focus(opts) {
            // A zero-width viewport means the panel is laid out inside a
            // hidden container; any zoom computed from it would be garbage, so
            // fall back to the fixed factor rather than framing on nothing.
            // `fitEl` (optional) is what to fit when it is not the box being
            // centred: a whole text column, say, with the selected line inside it.
            const fitEl = (opts && opts.fitEl) || boxEl;
            // The panel builds a NEW wrap on every selection, at its default
            // width, while `zoom` still holds the previous selection's value.
            // Give the wrap that width first, or the measurement below is at
            // zoom 1 and gets multiplied by the old zoom: each new selection
            // zoomed further in, up to the maximum.
            applyZoomWidth();
            if (opts && opts.fit && wrapEl && fitEl
                && fitEl.offsetWidth > 0 && viewport.clientWidth > 0) {
                // offsetWidth is the box at the CURRENT zoom, so scale relative
                // to it rather than treating it as a zoom-1 measurement.
                const want = viewport.clientWidth * (opts.fill || 0.9);
                zoom = clamp(zoom * (want / fitEl.offsetWidth));
            } else {
                zoom = cfg.focus;
            }
            // Not centerOnBox() alone: it returns early without a box, leaving
            // the new zoom set but never applied to the wrap.
            if (boxEl && opts && opts.align === 'top') topOnBox();
            else if (boxEl) centerOnBox();
            else applyZoomWidth();
        },
        get zoom() { return zoom; },
        centerOnBox,
        setZoomAtPoint,
        zoomIn: () => stepZoom(cfg.step),
        zoomOut: () => stepZoom(1 / cfg.step),
        reset,
        // Fullscreen toggles the Fullscreen API directly on the viewport rather
        // than moving the image into a separate overlay -- the element stays
        // put in the DOM, so the zoom/pan/drag listeners already bound to it
        // keep working unchanged while it's promoted to fill the screen.
        toggleFullscreen() {
            if (document.fullscreenElement === viewport) document.exitFullscreen();
            else viewport.requestFullscreen();
        },
        isFullscreen() { return document.fullscreenElement === viewport; },
        destroy() {
            viewport.removeEventListener('wheel', onWheel);
            viewport.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        },
    };
}
